import { motion, useReducedMotion } from 'motion/react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  type AutomationRunsListOpenedLocation,
  useTrackAutomationRunsListOpened,
  useTrackAutomationTasksSectionToggled,
} from '@air/analytics';

import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { PrivateWorkspaceAgentOnboardingReviewTaskRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/PrivateWorkspaceAgentOnboardingReviewTaskRow';
import { PrivateWorkspaceAgentPanelAutomationRunsView } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/PrivateWorkspaceAgentPanelAutomationRunsView';
import { PrivateWorkspaceAgentPanelMyTasksAutomationRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/PrivateWorkspaceAgentPanelMyTasksAutomationRow';
import { PrivateWorkspaceAgentPanelMyTasksRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/PrivateWorkspaceAgentPanelMyTasksRow';
import {
  type WorkspaceAgentPanelMyTasksAutomationRowRenderProps,
  WorkspaceAgentPanelMyTasksList,
  type WorkspaceAgentPanelMyTasksOnboardingReviewTask,
  type WorkspaceAgentPanelMyTasksSessionRowRenderProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksList';
import {
  getWorkspaceAgentPanelViewMotionTransition,
  workspaceAgentPanelMyTasksSubviewTransitionVariants,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelViewTransition';
import { usePrivateWorkspaceAgentOnboardingReviewTaskVisibility } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/onboarding/usePrivateWorkspaceAgentOnboardingReviewTaskVisibility';
import { usePrivateWorkspaceAgentPanelAutomationLatestRuns } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPanelAutomationLatestRuns';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { usePrivateAutomationsList } from '~/hooks/queries/automations/usePrivateAutomationsList';
import { useAutomationsAccess } from '~/hooks/useAutomationsAccess';

type WorkspaceAgentPanelMyTasksSubview = keyof typeof workspaceAgentPanelMyTasksSubviewTransitionVariants;

export const PrivateWorkspaceAgentPanelMyTasks = memo(() => {
  const session = usePrivateWorkspaceAgentPanelSession();
  const workspaceId = session.workspaceId;
  const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
  const setOnboardingHandoff = useWorkspaceAgentPanelStore((state) => state.setOnboardingHandoff);
  const clearBoardTemplatesFlowBoard = useWorkspaceAgentPanelStore((state) => state.clearBoardTemplatesFlowBoard);
  const closeGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.closeGuidedVariantFlow);
  const setPendingThreadCreatedLocation = useWorkspaceAgentPanelStore((state) => state.setPendingThreadCreatedLocation);
  const pendingAutomationTasksFocusId = useWorkspaceAgentPanelStore((state) => state.pendingAutomationTasksFocusId);
  const clearPendingAutomationTasksFocusId = useWorkspaceAgentPanelStore(
    (state) => state.clearPendingAutomationTasksFocusId,
  );
  const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const { onboardingReviewTaskVisibility } = usePrivateWorkspaceAgentOnboardingReviewTaskVisibility();
  const { showAutomationsChrome } = useAutomationsAccess();
  const [selectedAutomationId, setSelectedAutomationId] = useState<string | null>(null);
  const [isAutomationsSectionExpanded, setIsAutomationsSectionExpanded] = useState(false);
  const [focusAutomationRowId, setFocusAutomationRowId] = useState<string | null>(null);
  const returnFocusAutomationIdRef = useRef<string | null>(null);
  const hasPlayedSubviewEnterRef = useRef<boolean>();
  const { trackAutomationRunsListOpened } = useTrackAutomationRunsListOpened();
  const { trackAutomationTasksSectionToggled } = useTrackAutomationTasksSectionToggled();

  const {
    data: automationsData,
    fetchNextPage: fetchNextAutomationsPage,
    hasNextPage: hasNextAutomationsPage,
    isFetchingNextPage: isFetchingNextAutomationsPage,
    isLoading: isLoadingAutomations,
  } = usePrivateAutomationsList({ limit: 50, workspaceId }, { enabled: showAutomationsChrome });

  const automations = useMemo(() => automationsData?.pages.flatMap((page) => page.data) ?? [], [automationsData]);
  const showAutomationsSection = showAutomationsChrome && (isLoadingAutomations || automations.length > 0);
  const automationIds = useMemo(() => automations.map((automation) => automation.id), [automations]);

  const { isLoadingLatestRuns, latestRunByAutomationId } = usePrivateWorkspaceAgentPanelAutomationLatestRuns({
    automationIds,
    enabled: showAutomationsSection && automations.length > 0 && isAutomationsSectionExpanded,
    workspaceId,
  });

  const subview: WorkspaceAgentPanelMyTasksSubview = selectedAutomationId && workspaceId ? 'runs' : 'list';
  const shouldReduceMotion = useReducedMotion();
  const subviewTransition = getWorkspaceAgentPanelViewMotionTransition(shouldReduceMotion === true);
  const subviewEnterInitial = hasPlayedSubviewEnterRef.current
    ? workspaceAgentPanelMyTasksSubviewTransitionVariants[subview].initial
    : false;

  useIsomorphicLayoutEffect(() => {
    hasPlayedSubviewEnterRef.current = true;
  }, []);

  const handleStartNewTask = useCallback(() => {
    if (blockIfCreditsDepleted(() => showChat({ focusComposer: false }))) {
      return;
    }

    setPendingThreadCreatedLocation('tasks_list');
    // The intro's simulated thread lives in memory, not in the session. A new thread drops it.
    setOnboardingHandoff(null);
    clearBoardTemplatesFlowBoard();
    closeGuidedVariantFlow();
    session.resetSession();
    showChat();
  }, [
    blockIfCreditsDepleted,
    clearBoardTemplatesFlowBoard,
    closeGuidedVariantFlow,
    setOnboardingHandoff,
    setPendingThreadCreatedLocation,
    showChat,
    session.resetSession,
  ]);

  const handleAutomationOpen = useCallback(
    (automationId: string, location: AutomationRunsListOpenedLocation) => {
      trackAutomationRunsListOpened({ automation_id: automationId, location });
      returnFocusAutomationIdRef.current = automationId;
      setIsAutomationsSectionExpanded(true);
      setSelectedAutomationId(automationId);
    },
    [trackAutomationRunsListOpened],
  );

  const handleAutomationOpenFromTasksList = useCallback(
    (automationId: string) => {
      handleAutomationOpen(automationId, 'agent_tasks_list');
    },
    [handleAutomationOpen],
  );

  useIsomorphicLayoutEffect(() => {
    if (!pendingAutomationTasksFocusId) {
      return;
    }

    handleAutomationOpen(pendingAutomationTasksFocusId, 'automation_detail');
    clearPendingAutomationTasksFocusId();
  }, [clearPendingAutomationTasksFocusId, handleAutomationOpen, pendingAutomationTasksFocusId]);

  const renderSessionRow = useCallback(
    ({ bucket, session: rowSession }: WorkspaceAgentPanelMyTasksSessionRowRenderProps) => {
      return <PrivateWorkspaceAgentPanelMyTasksRow bucket={bucket} session={rowSession} />;
    },
    [],
  );

  const renderAutomationRow = useCallback(
    ({ automation }: WorkspaceAgentPanelMyTasksAutomationRowRenderProps) => {
      return (
        <PrivateWorkspaceAgentPanelMyTasksAutomationRow
          automation={automation}
          latestRun={latestRunByAutomationId.get(automation.id)}
          onOpen={handleAutomationOpenFromTasksList}
        />
      );
    },
    [handleAutomationOpenFromTasksList, latestRunByAutomationId],
  );

  const renderOnboardingReviewTask = useCallback(() => <PrivateWorkspaceAgentOnboardingReviewTaskRow />, []);

  const onboardingReviewTask = useMemo<WorkspaceAgentPanelMyTasksOnboardingReviewTask | undefined>(
    () =>
      onboardingReviewTaskVisibility === 'hidden'
        ? undefined
        : { placement: onboardingReviewTaskVisibility, render: renderOnboardingReviewTask },
    [onboardingReviewTaskVisibility, renderOnboardingReviewTask],
  );

  const handleAutomationRunsBack = useCallback(() => {
    const automationIdForFocus = returnFocusAutomationIdRef.current;
    setSelectedAutomationId(null);

    if (automationIdForFocus !== null) {
      setFocusAutomationRowId(automationIdForFocus);
    }
  }, []);

  const handleFocusAutomationRowComplete = useCallback(() => {
    setFocusAutomationRowId(null);
    returnFocusAutomationIdRef.current = null;
  }, []);

  const handleAutomationsSectionExpandedChange = useCallback(
    (isExpanded: boolean) => {
      trackAutomationTasksSectionToggled({
        automation_count: automations.length,
        is_expanded: isExpanded,
      });
      setIsAutomationsSectionExpanded(isExpanded);
    },
    [automations.length, trackAutomationTasksSectionToggled],
  );

  return (
    <motion.div
      animate={workspaceAgentPanelMyTasksSubviewTransitionVariants[subview].animate}
      className="flex min-h-0 flex-1 flex-col"
      initial={subviewEnterInitial}
      key={subview}
      transition={subviewTransition}
    >
      {subview === 'runs' && selectedAutomationId && workspaceId ? (
        <PrivateWorkspaceAgentPanelAutomationRunsView
          automationId={selectedAutomationId}
          isStartNewTaskDisabled={isCreditsDepleted}
          onBack={handleAutomationRunsBack}
          onStartNewTask={handleStartNewTask}
          workspaceId={workspaceId}
        />
      ) : (
        <WorkspaceAgentPanelMyTasksList
          automations={automations}
          fetchNextAutomationsPage={fetchNextAutomationsPage}
          fetchNextArchivedSessionsPage={session.fetchNextArchivedSessionsPage}
          fetchNextLiveSessionsPage={session.fetchNextLiveSessionsPage}
          focusAutomationRowId={focusAutomationRowId}
          hasNextAutomationsPage={hasNextAutomationsPage}
          hasNextArchivedSessionsPage={session.hasNextArchivedSessionsPage}
          hasNextLiveSessionsPage={session.hasNextLiveSessionsPage}
          isFetchingNextAutomationsPage={isFetchingNextAutomationsPage}
          isFetchingNextArchivedSessionsPage={session.isFetchingNextArchivedSessionsPage}
          isFetchingNextLiveSessionsPage={session.isFetchingNextLiveSessionsPage}
          isAutomationsSectionExpanded={isAutomationsSectionExpanded}
          isLoadingAutomations={isLoadingAutomations || isLoadingLatestRuns}
          isLoadingSessions={session.isLoadingSessions}
          isStartNewTaskDisabled={isCreditsDepleted}
          onboardingReviewTask={onboardingReviewTask}
          onAutomationsSectionExpandedChange={handleAutomationsSectionExpandedChange}
          onFocusAutomationRowComplete={handleFocusAutomationRowComplete}
          onStartNewTask={handleStartNewTask}
          renderAutomationRow={renderAutomationRow}
          renderSessionRow={renderSessionRow}
          sessions={session.sessions}
          showAutomationsSection={showAutomationsSection}
        />
      )}
    </motion.div>
  );
});

PrivateWorkspaceAgentPanelMyTasks.displayName = 'PrivateWorkspaceAgentPanelMyTasks';
